import { Alert, Card } from '@mui/material';
import { Box } from '@mui/system';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { useAuth } from 'hooks';
import { getRanks } from 'lib/firebase';
import { getSupplier } from 'lib/napi';
import { useEffect, useState } from 'react';
import { Supplier } from 'types';
import { RankingCards } from './components/RankingCards/RankingCards';
import { RankingGraph } from './components/RankingGraph/RankingGraph';

interface EfficiencyRankProps {
    supplierId?: string;
}

export type RankData = {
    rank: number;
    total: number;
    percentile: number;
};

export const EfficiencyRank = ({ supplierId }: EfficiencyRankProps) => {
    const { user } = useAuth();
    const [supplierInfo, setSupplierInfo] = useState<Supplier | null>(null);
    const [allRankings, setAllRankings] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rankData, setRankData] = useState<RankData>({
        rank: 0,
        total: 0,
        percentile: 0,
    });

    useEffect(() => {
        if (supplierId !== undefined && user?.idToken) {
            setIsLoading(true);
            getSupplier(supplierId, user.idToken)
                .then((result) => {
                    setSupplierInfo(result);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }
    }, [supplierId, user]);

    useEffect(() => {
        if (supplierInfo?.info?.rank) {
            const rank = supplierInfo.info.rank;
            getRanks()
                .then((result) => {
                    if (result) {
                        setAllRankings(result);

                        const total = result.length;
                        const percentile = Math.round((1 - rank / total) * 100);

                        setRankData({
                            rank,
                            total,
                            percentile,
                        });
                    }
                })
                .finally(() => setIsLoading(false));
        } else if (supplierInfo && !supplierInfo?.info?.rank) {
            setIsLoading(false);
        }
    }, [supplierInfo]);

    const isNoData =
        !isLoading &&
        (!supplierInfo ||
            Object.values(rankData).every((datum) => datum === 0));

    return (
        <div data-testid="EfficiencyRank">
            {isNoData && (
                <Alert severity="error" sx={{ margin: '1rem' }}>
                    Sorry, we were unable to collect enough data to determine
                    your ranking.
                </Alert>
            )}
            {!isNoData && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap-reverse',
                    }}
                >
                    <Box
                        sx={{
                            padding: '1rem',
                            flexGrow: '1',
                            maxWidth: '500px',
                        }}
                    >
                        <Card sx={{ padding: '1rem', height: 'fit-content' }}>
                            <Box sx={{ width: '100%' }}>
                                {isLoading ? (
                                    <LoadingSpinner
                                        style={{ margin: 'auto' }}
                                    />
                                ) : (
                                    <RankingGraph
                                        supplierRank={rankData.rank}
                                        ranks={allRankings}
                                    />
                                )}
                            </Box>
                        </Card>
                    </Box>
                    <Box sx={{ padding: '1rem', flexGrow: '1' }}>
                        <RankingCards
                            rankData={rankData}
                            isLoading={isLoading}
                        />
                    </Box>
                </Box>
            )}
        </div>
    );
};
